import {
  Mesh,
  DoubleSide,
  MeshBasicMaterial,
  TextureLoader,
  Vector3,
  PlaneGeometry,
  BoxGeometry,
  SphereGeometry,
  CircleGeometry,
  FrontSide,
  Sprite,
  SpriteMaterial,
} from 'three';

import { gsap } from 'gsap';

import { VRObject3D } from './VRObject3D';
import { cornerHandleLocalFromVertex, htmlEscaper, vertexLocalFromCornerHandle } from './helpers/worldHelpers';
import { crossedCameraTexture, dragTexture, emptyTexture, INDICATOR_HIT_SIZE, imageGallery as galleryIconTexture, meshTextures, myLocationTexture } from './helpers/texturesAndGeometries';
import { tourExists } from '../../socket/socketHelper';
import { tourID } from '../../setScene';
import { CUSTOM_TOUR_EVENTS } from '../../socket/Events';
import { SCENE_EVENTS } from '../MVC/Model';
import ModalHandler from './helpers/ModalHandler.js';
import {
  GALLERY_MODE,
  clampGallerySlideIndex,
  formatGalleryUploadError,
  galleryHasImages,
  galleryHasMultipleImages,
  getGalleryCoverUrl,
  getGalleryImageRotation,
  getGalleryImageUrl,
  getGallerySlideDurationMs,
  getGalleryUploadJobId,
  normalizeGalleryImage,
  normalizeGalleryImages,
  persistGalleryConfig,
  reorderGalleryImages,
  rotateGalleryImage,
  shouldGalleryAutoplay,
} from './helpers/imageGalleryHelper';

const modalWrapper = (uuid) => `
<div class="modal image-gallery" id="image-gallery-${uuid}" >
  <div class="modal-background"></div>
  <div class="modal-content modal-content-image-gallery modal-content--full-screen-mobile">
    <header>
      <div class="close-modal clickable custom-close-modal" role="button" tabindex="0" aria-label="Close gallery">
        <span class="material-icons md-32" aria-hidden="true">close</span>
      </div>
    </header>
    <section class="image-gallery-content">
    </section>
  </div>
</div>
`

const uploadedTitle = () => `
<div class="flx-sb">
  <label id="uploadedTitleImages" data-lang="upload-image-text">Images:</label>
</div>
`

const galleryLangTitle = (key, fallback) => (
  window.multiLanguagesController?.languageObject?.[key]?.title || fallback
);

const uploadedTemplate = (url, rotation, index) => {
  const rot = getGalleryImageRotation({ url, rotation });
  const rotateLeft = htmlEscaper(galleryLangTitle('gallery-rotate-left-title', 'Rotate left'));
  const rotateRight = htmlEscaper(galleryLangTitle('gallery-rotate-right-title', 'Rotate right'));
  const deleteTitle = htmlEscaper(galleryLangTitle('delete-img-title', 'Delete image'));
  return `
<div class="image_container_image_gallery" draggable="true" data-gallery-index="${index}">
    <span class="material-icons-outlined gallery-thumb-drag">drag_indicator</span>
    <div class="gallery-thumb-preview">
      <img class="image_gallery_image default_image" src="${url}" style="transform: rotate(${rot}deg)">
    </div>
    <div class="gallery-thumb-actions">
      <span class="material-icons-outlined rotate_gallery_image_left" title="${rotateLeft}" data-lang="gallery-rotate-left-title">rotate_left</span>
      <span class="material-icons-outlined rotate_gallery_image_right" title="${rotateRight}" data-lang="gallery-rotate-right-title">rotate_right</span>
      <span class="material-icons-outlined upload-thumb-removal delete_gallery_image" title="${deleteTitle}" data-lang="delete-img-title">delete</span>
    </div>
</div>
`;
};

const galleryUploadJobTemplate = (job) => {
  const id = htmlEscaper(job.id || '') || '';
  const name = htmlEscaper(job.fileName || '') || '';
  if (job.status === 'error') {
    const error = htmlEscaper(job.errorMessage || '') || '';
    const dismissTitle = htmlEscaper(typeof $trans === 'function' ? $trans('gallery-upload-dismiss-title') : 'Dismiss') || '';
    return `
<div class="image_container_image_gallery gallery-upload-job is-error" data-job-id="${id}">
  <div class="gallery-upload-job-body">
    <div class="gallery-upload-copy">
      <span class="gallery-upload-filename">${name}</span>
      <span class="gallery-upload-error">${error}</span>
    </div>
    <span class="material-icons-outlined upload-thumb-removal gallery-upload-dismiss" title="${dismissTitle}">close</span>
  </div>
</div>`;
  }
  const uploading = htmlEscaper(typeof $trans === 'function' ? $trans('gallery-uploading-text') : 'Uploading…') || '';
  const pct = Math.max(0, Math.min(100, Math.round(job.progress || 0)));
  return `
<div class="image_container_image_gallery gallery-upload-job is-uploading" data-job-id="${id}">
  <div class="gallery-upload-job-body">
    <span class="gallery-upload-filename">${name}</span>
    <span class="gallery-upload-status">${uploading} ${pct}%</span>
  </div>
  <div class="gallery-upload-progress"><span class="gallery-upload-progress-bar" style="width:${pct}%"></span></div>
</div>`;
};

const template = (uuid) => {
  return `
    <div id="gallery-${uuid}" class="gallery" tabindex="-1">
      <div id="left" class="nav-button" role="button" tabindex="0">
        <span class="material-icons" aria-hidden="true">chevron_left</span>
      </div>
      <div id="imageContainer" class="image-container">
        <div class="container" >
        </div>
        <div class="gallery-loading-overlay" hidden>
          <div class="gallery-loading-overlay__spinner" aria-hidden="true"></div>
          <span class="gallery-loading-overlay__text" data-lang="gallery-image-loading-text">Loading…</span>
        </div>
      </div>
      <div id="right" class="nav-button" role="button" tabindex="0">
        <span class="material-icons" aria-hidden="true">chevron_right</span>
      </div>
    </div>
  `;
}

class VRImageGallery extends VRObject3D {

  constructor(containerName, camera, texture, x, y, z, model) {
    super(containerName);
    this.objConfig.dragOnlyWhenSelected = true;
    this.objConfig.isResizable = true;
    this.objConfig.isRotatable = false;
    this.objConfig.cubeSize = 20
    this.objConfig.offset = {
      topLeft: { x: 2, y: -3.5 },
      topRight: { x: -2, y: -3.5 },
      bottomRight: { x: -2, y: 3.5 },
      bottomLeft: { x: 2, y: 3.5 }
    }
    this.limits = {
      min: 200,   // New min value
      max: 900,   // New max value
      distance: 500 // New distance value
    };
    // PlaneGeometry(64,64) corners: topLeft(-32,32), topRight(32,32), bottomRight(32,-32), bottomLeft(-32,-32)
    const halfSize = 32;
    this.perspectivePosition = {
      topLeft: new Vector3(-halfSize, halfSize, 0),
      topRight: new Vector3(halfSize, halfSize, 0),
      bottomRight: new Vector3(halfSize, -halfSize, 0),
      bottomLeft: new Vector3(-halfSize, -halfSize, 0),
    };
    this.originalPerspectivePosition = {
      topLeft: new Vector3(-halfSize, halfSize, 0),
      topRight: new Vector3(halfSize, halfSize, 0),
      bottomRight: new Vector3(halfSize, -halfSize, 0),
      bottomLeft: new Vector3(-halfSize, -halfSize, 0),
    };

    this.dragCubeCorners = {};
    this.dragSprites = {};
    this.cornerPoints = {};
    this.centerHandle = null;
    this.centerHandleSprite = null;

    this.model = model;
    this.camera = camera;
    this.material = new MeshBasicMaterial({ side: DoubleSide, transparent: true, map: texture });
    this.geometry = new PlaneGeometry(64, 64);
    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.type = 'imageGallery';
    this.mesh.name = 'imageGallery';
    this.name = 'imageGallery';
    // this.mesh.rotation.z = -Math.PI / 2; // Rotate -90 degrees around the Z-axis
    this.borderMesh = new Mesh(this.geometry, new MeshBasicMaterial({ side: FrontSide, transparent: true, color: "white", opacity: 0 }));
    this.borderMesh.name = "imageGalleryBorder"
    if (!this.editMode) {
      this.mesh.add(this.borderMesh);
    }

    this.images = [];
    this.imageItems = [];
    this.templates = [];
    this.tempUpload = [];
    this.uploadJobs = [];
    this._galleryDragIndex = null;
    this.useIcon = false;
    this.customIcon = true;
    this.defaultTexture = texture;
    this.loader = new TextureLoader();
    this.imageLoadQueue = []; // Initialize the load queue
    this.isLoadingImage = false; // Flag to track loading state
    this.imageJustLoaded = false; // Flag to indicate that an image has just been loaded

    // INIT
    this.camera !== null ? this._setInitialPosRot() : this.setPosition(x, y, z);

    this.add(this.mesh);
    this.mesh.groupDetails = { title: $trans('info-element'), icon: 'info', editTitle: $trans('edit-image-gallery-element'), dataLang: 'edit-info-element' }

    this.editControls();
    this.updateDragBehavior(true);

    this.uuid = new aptis.UUIDGenerator().generate();

    this.model.addEventListener(CUSTOM_TOUR_EVENTS.CUSTOM_SLIDE, (e) => {
      this.goToSlide(e.data.dbConfig.currSlide);
    });
    this.model.addEventListener(SCENE_EVENTS.TINY_WORLD_ANIMATION_ENDED, this.restoreMeshVisibility);
    this.model.addEventListener(SCENE_EVENTS.STOP_SCENE_CHANGE, this.restoreMeshVisibility);

    // this.modalWrapper = $("#image-gallery");
    this.modalWrapper = $(modalWrapper(this.uuid));
    $("#embeddedWebpageModal").after(this.modalWrapper);
    const imageGallery = new ModalHandler(this.modalWrapper[0]);

    this.closeBtn = this.modalWrapper.find(".close-modal");
    this.closeBtn.on("click", this.closeModal);
    const $template = $(template(this.uuid));
    // this.mainContent = $("#image-gallery-content");
    this.mainContent = this.modalWrapper.find(".image-gallery-content")
    this.mainContent.append($template);

    this.overlayTemplate = $template;
    this.prevButton = $template.find('#left');
    this.nextButton = $template.find('#right');
    this.loadingOverlay = $template.find('.gallery-loading-overlay');
    this.curSlide = 0;
    this.maxSlide = this.images.length;
    this.meshSlideIndex = 0;
    this.meshSlideShowInterval = null;
    this.slideShowInterval = null;
    this.isOverlayOpen = false;
    this.isSlideshowPlaying = false;
    this._bindOverlayControls();
  }

  changeAutoLoop(value) {
    const galleryMode = (value === '2' || value === GALLERY_MODE.AUTOPLAY)
      ? GALLERY_MODE.AUTOPLAY
      : GALLERY_MODE.STATIC;
    this.dbConfig = persistGalleryConfig({
      ...this.dbConfig,
      galleryMode,
    });
    this.stopMeshSlideShow();
  }

  stopMeshSlideShow() {
    if (this.meshSlideShowInterval) {
      clearInterval(this.meshSlideShowInterval);
      this.meshSlideShowInterval = null;
    }
  }

  startMeshSlideShow() {
    this.stopMeshSlideShow();
    if (this.images.length <= 1 || this.model.editMode) return;
    this.meshSlideShowInterval = setInterval(() => {
      if (this.imageJustLoaded) {
        this.imageJustLoaded = false;
        return;
      }
      this.meshSlideIndex = (this.meshSlideIndex + 1) % this.images.length;
      this.setAvatarImage(this.images[this.meshSlideIndex]);
    }, 3000);
  }

  isClickableInView() {
    if (this.model?.editMode) return false;
    if (!this.mesh || this.mesh.visible === false) return false;
    if (this.userData?.hiddenBySceneAction) return false;
    if (this.dbConfig?.hideOnStart) return false;
    if (this.model?.showElements === false) return false;
    const mat = this.mesh.material;
    if (mat && !Array.isArray(mat) && typeof mat.opacity === 'number' && mat.opacity <= 0.01) {
      return false;
    }
    return true;
  }

  restoreMeshVisibility = () => {
    if (!this.mesh) return;
    if (this.useIcon && !this.model.editMode) return;
    if (!this.model.editMode) {
      const hiddenByConfig = this.dbConfig?.hideOnStart === true;
      const hiddenByGlobalToggle = this.model.showElements === false;
      if (hiddenByConfig || hiddenByGlobalToggle) return;
    }
    this.mesh.visible = true;
    if (this.mesh.material && !Array.isArray(this.mesh.material)) {
      this.mesh.material.opacity = 1;
      this.mesh.material.needsUpdate = true;
    }
  }

  getLimits() {
    const min = Number(this.limits?.min) || 200;
    const max = Number(this.limits?.max) || 900;
    const configuredDistance = Number(this.dbConfig?.distance);
    const runtimeDistance = Number(this.limits?.distance);
    const distance = Number.isFinite(configuredDistance) && configuredDistance > 0
      ? configuredDistance
      : (Number.isFinite(runtimeDistance) && runtimeDistance > 0 ? runtimeDistance : 500);

    return { min, max, distance };
  }

  setDistance(val) {
    const n = Number(val);
    if (!Number.isFinite(n) || n <= 0) return;
    const { min, max } = this.getLimits();
    const clamped = Math.min(max, Math.max(min, n));
    this.limits.distance = clamped;
    this.dbConfig = { ...this.dbConfig, distance: clamped };
  }

  isIndicatorMode = () => {
    return !!this.useIcon || !this.customIcon;
  }

  applyIndicatorHitGeometry = () => {
    this.updateMeshGeometry(INDICATOR_HIT_SIZE, INDICATOR_HIT_SIZE);
    this.resetPerspectivePositions(INDICATOR_HIT_SIZE, INDICATOR_HIT_SIZE);
    this.applyPerspectiveDragAndDrop();
  }

  restorePerspectiveFromDbConfig = () => {
    ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'].forEach((name) => {
      const prop = this.dbConfig?.[name];
      if (prop) {
        this.perspectivePosition[name] = new Vector3(prop.x, prop.y, prop.z || 0);
      }
      const orig = this.dbConfig?.original?.[name];
      if (orig) {
        this.originalPerspectivePosition[name] = new Vector3(orig.x, orig.y, orig.z || 0);
      }
    });
  }

  hasSavedPerspectivePositions = () => {
    const corners = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
    return corners.every((name) => {
      const p = this.dbConfig?.[name];
      return p && Number.isFinite(p.x) && Number.isFinite(p.y);
    });
  }

  setUseIcon = (useIcon) => {
    const next = !!useIcon;
    if (this.useIcon === next) {
      this.customIcon = !next;
      this.objConfig.isResizable = !next;
      return;
    }

    this.useIcon = next;
    this.customIcon = !next;
    this.objConfig.isResizable = !next;
    if (this.dbConfig) this.dbConfig.useIcon = this.useIcon;

    if (this.useIcon) {
      this.updateDragBehavior();
      this.updateMeshTexture();
      return;
    }

    this.restorePerspectiveFromDbConfig();
    this.updateDragBehavior();
    this.updateMeshTexture(null, !this.hasSavedPerspectivePositions());
  }

  updateDragBehavior = (isInitializing = false) => {
    if (this.useIcon) {
      this.objConfig.dragOnlyWhenSelected = false;
      this.objConfig.isResizable = false;
      if (this.model?.editMode) {
        if (!isInitializing) {
          this.applyIndicatorHitGeometry();
          this.removeDragPoints();
        }
        if (this.isSelected) {
          this.hideDragHandles();
        }
      }
      return;
    }

    this.objConfig.dragOnlyWhenSelected = true;
    this.objConfig.isResizable = true;
    if (this.model?.editMode) {
      if (!isInitializing) {
        this.addDragPoints();
      }
      if (this.isSelected && this.dbConfig?.showDragCorners !== false) {
        this.showDragHandles();
      }
    }
  }

  updateMeshTexture = (imageLoadCallback = null, resetPerspective = false) => {
    if (this.useIcon) {
      this.material.map = emptyTexture;
      this.material.needsUpdate = true;
      this.applyIndicatorHitGeometry();
      imageLoadCallback && imageLoadCallback();
      return;
    }

    const coverUrl = getGalleryCoverUrl(this.dbConfig);
    if (coverUrl) {
      this.setAvatarImage(coverUrl, imageLoadCallback, resetPerspective);
      return;
    }

    // Preview mode with no asset yet: keep the existing gallery icon on the mesh.
    this.material.map = galleryIconTexture || this.defaultTexture;
    this.material.needsUpdate = true;
    if (resetPerspective) {
      this.updateMeshGeometry(64, 64);
      this.resetPerspectivePositions(64, 64);
    }
    this.applyPerspectiveDragAndDrop();
    imageLoadCallback && imageLoadCallback();
  }

  createUITemplates = () => {
    this.imageItems = normalizeGalleryImages([...this.imageItems, ...this.tempUpload]);
    this.tempUpload = [];
    this.images = this.imageItems.map(getGalleryImageUrl);
    this.dbConfig = persistGalleryConfig({ ...this.dbConfig, images: this.imageItems });
    this._renderGalleryImagesList();
    this._syncOverlayImages();
    if (this.model.editMode && !this.useIcon) {
      const coverUrl = getGalleryCoverUrl(this.dbConfig);
      if (coverUrl) {
        this.setAvatarImage(coverUrl);
      }
    }
  }

  _renderGalleryImagesList = () => {
    const container = $(".view_gallery_modal_edit .gallery-images-list");
    if (!container.length) return;

    $('#uploadedTitleImages').remove();
    container.find('.image_container_image_gallery, .gallery-upload-job').remove();
    container.append($(uploadedTitle()));

    this.templates = [];
    this.imageItems.forEach((item, index) => {
      const src = getGalleryImageUrl(item);
      const $template = $(uploadedTemplate(src, getGalleryImageRotation(item), index));
      this.templates.push($template);
      container.append($template);
    });

    (this.uploadJobs || []).forEach((job) => {
      container.append($(galleryUploadJobTemplate(job)));
    });

    this._bindGalleryThumbInteractions(container);
    this._syncEmptyGalleryHint();
  }

  _syncEmptyGalleryHint = () => {
    $('#gallery_images_required_hint').toggle(!galleryHasImages(this.imageItems));
  }

  _galleryThumbIndex = (el) => {
    const index = Number($(el).closest('[data-gallery-index]').attr('data-gallery-index'));
    return Number.isInteger(index) ? index : -1;
  }

  _bindGalleryThumbInteractions = (container) => {
    container.off('.galleryThumbs');

    container.on('click.galleryThumbs', '.gallery-upload-dismiss', (e) => {
      const id = $(e.currentTarget).closest('[data-job-id]').attr('data-job-id');
      this.dismissGalleryUpload(id);
    });

    container.on('click.galleryThumbs', '.rotate_gallery_image_left', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.rotateGalleryThumb(this._galleryThumbIndex(e.currentTarget), -1);
    });

    container.on('click.galleryThumbs', '.rotate_gallery_image_right', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.rotateGalleryThumb(this._galleryThumbIndex(e.currentTarget), 1);
    });

    container.on('click.galleryThumbs', '.delete_gallery_image', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const index = this._galleryThumbIndex(e.currentTarget);
      const item = this.imageItems[index];
      if (!item) return;
      const message = typeof $trans === 'function'
        ? $trans('confirm-delete-gallery-image')
        : 'Are you sure you want to delete this image?';
      this.model.confirm(message, () => {
        const currentIndex = this.imageItems.indexOf(item);
        if (currentIndex < 0) return;
        this.imageItems.splice(currentIndex, 1);
        this.createUITemplates();
      });
    });

    container.on('dragstart.galleryThumbs', '.image_container_image_gallery[data-gallery-index]', (e) => {
      if ($(e.target).closest('.gallery-thumb-actions').length) {
        e.preventDefault();
        return;
      }
      this._galleryDragIndex = this._galleryThumbIndex(e.currentTarget);
      const dataTransfer = e.originalEvent?.dataTransfer;
      if (dataTransfer) {
        dataTransfer.effectAllowed = 'move';
        dataTransfer.setData('text/plain', String(this._galleryDragIndex));
      }
      $(e.currentTarget).addClass('is-dragging');
    });

    container.on('dragend.galleryThumbs', '.image_container_image_gallery[data-gallery-index]', (e) => {
      $(e.currentTarget).removeClass('is-dragging');
      container.find('.dragover').removeClass('dragover');
    });

    container.on('dragover.galleryThumbs', '.image_container_image_gallery[data-gallery-index]', (e) => {
      e.preventDefault();
      const dataTransfer = e.originalEvent?.dataTransfer;
      if (dataTransfer) dataTransfer.dropEffect = 'move';
      $(e.currentTarget).addClass('dragover');
    });

    container.on('dragleave.galleryThumbs', '.image_container_image_gallery[data-gallery-index]', (e) => {
      $(e.currentTarget).removeClass('dragover');
    });

    container.on('drop.galleryThumbs', '.image_container_image_gallery[data-gallery-index]', (e) => {
      e.preventDefault();
      const $target = $(e.currentTarget);
      $target.removeClass('dragover');
      if (e.originalEvent?.dataTransfer?.files?.length) return;
      e.stopPropagation();
      this.reorderGalleryThumb(this._galleryDragIndex, this._galleryThumbIndex($target));
      this._galleryDragIndex = null;
    });
  }

  rotateGalleryThumb = (index, direction) => {
    const rotated = rotateGalleryImage(this.imageItems[index], direction);
    if (!rotated) return;
    this.imageItems[index] = rotated;
    this.createUITemplates();
  }

  reorderGalleryThumb = (fromIndex, toIndex) => {
    if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex) || fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    this.imageItems = reorderGalleryImages(this.imageItems, fromIndex, toIndex);
    this.createUITemplates();
  }

  _syncOverlayImages = () => {
    const $container = this.modalWrapper?.find('#imageContainer .container');
    if (!$container?.length) return;

    const bySrc = new Map();
    $container.children('.pic').each((_, wrap) => {
      const src = wrap.querySelector?.('.pic__media')?.getAttribute('src') || wrap.getAttribute('src');
      if (!src) return;
      if (!bySrc.has(src)) bySrc.set(src, []);
      bySrc.get(src).push(wrap);
    });

    this.imageItems.forEach((item) => {
      const src = getGalleryImageUrl(item);
      if (!src) return;
      const pool = bySrc.get(src) || [];
      let wrap = pool.shift();
      if (!wrap || wrap.tagName === 'IMG') {
        wrap?.remove?.();
        wrap = document.createElement('div');
        wrap.className = 'pic';
        const media = new Image();
        media.className = 'pic__media';
        wrap.appendChild(media);
      }
      const media = wrap.querySelector('.pic__media');
      wrap.dataset.rotation = String(getGalleryImageRotation(item));
      if (media.getAttribute('src') !== src) media.src = src;
      this._watchOverlayImageLoad(media, src);
      $container.append(wrap);
    });

    bySrc.forEach((pool) => pool.forEach((el) => el.remove()));
    this.maxSlide = this.imageItems.length;
    if (this.isOverlayOpen && this.imageItems.length) {
      this.goToSlide(this.curSlide);
    }
  }

  _upsertGalleryUploadJob = (file, patch) => {
    const id = getGalleryUploadJobId(file);
    if (!id) return null;
    this.uploadJobs = this.uploadJobs || [];
    let job = this.uploadJobs.find((item) => item.id === id);
    if (!job) {
      job = {
        id,
        fileName: file?.name || '',
        status: 'uploading',
        progress: 0,
        errorMessage: '',
      };
      this.uploadJobs.push(job);
    }
    Object.assign(job, patch);
    return job;
  }

  startGalleryUpload = (file) => {
    this._upsertGalleryUploadJob(file, { status: 'uploading', progress: 0, errorMessage: '' });
    this._renderGalleryImagesList();
  }

  updateGalleryUploadProgress = (file, progress) => {
    const id = getGalleryUploadJobId(file);
    const existing = (this.uploadJobs || []).find((item) => item.id === id);
    if (existing?.status === 'error') return;

    const job = this._upsertGalleryUploadJob(file, {
      status: 'uploading',
      progress: Math.max(0, Math.min(100, Math.round(progress || 0))),
    });
    if (!job) return;

    const $row = $(".view_gallery_modal_edit .gallery-upload-job").filter((_, el) => (
      el.getAttribute('data-job-id') === job.id
    ));
    if ($row.length) {
      const uploading = typeof $trans === 'function' ? $trans('gallery-uploading-text') : 'Uploading...';
      $row.find('.gallery-upload-status').text(`${uploading} ${job.progress}%`);
      $row.find('.gallery-upload-progress-bar').css('width', `${job.progress}%`);
      return;
    }
    this._renderGalleryImagesList();
  }

  failGalleryUpload = (file, errorMessage) => {
    if (file?._galleryUploadCompleted) return;
    this._upsertGalleryUploadJob(file, {
      status: 'error',
      errorMessage: formatGalleryUploadError(file, errorMessage),
    });
    this._renderGalleryImagesList();
  }

  completeGalleryUpload = (url, file) => {
    if (file) file._galleryUploadCompleted = true;
    const id = getGalleryUploadJobId(file);
    if (id) {
      this.uploadJobs = (this.uploadJobs || []).filter((job) => job.id !== id);
    }
    this.addTempImage(url);
  }

  dismissGalleryUpload = (id) => {
    if (!id) return;
    this.uploadJobs = (this.uploadJobs || []).filter((job) => job.id !== id);
    this._renderGalleryImagesList();
  }

  addTempImage = (url) => {
    const item = normalizeGalleryImage(url);
    if (item) this.tempUpload.push(item);
    this.createUITemplates();
  }

  _overlayLabel = (key, fallback) => (
    typeof $trans === 'function' ? $trans(key) : fallback
  )

  _applyOverlayControlLabels = () => {
    const prev = this._overlayLabel('gallery-previous-image', 'Previous image');
    const next = this._overlayLabel('gallery-next-image', 'Next image');
    const close = this._overlayLabel('gallery-close', 'Close gallery');
    this.prevButton?.attr({ 'aria-label': prev, title: prev });
    this.nextButton?.attr({ 'aria-label': next, title: next });
    this.closeBtn?.attr({ 'aria-label': close, title: close });
  }

  _bindOverlayControls = () => {
    this._applyOverlayControlLabels();
    this.prevButton.off('.galleryOverlay').on('click.galleryOverlay', (e) => {
      e.preventDefault();
      this.showPreviousImage();
    }).on('keydown.galleryOverlay', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.showPreviousImage();
      }
    });
    this.nextButton.off('.galleryOverlay').on('click.galleryOverlay', (e) => {
      e.preventDefault();
      this.showNextImage();
    }).on('keydown.galleryOverlay', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.showNextImage();
      }
    });
    this.closeBtn.off('keydown.galleryOverlay').on('keydown.galleryOverlay', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.closeModal();
      }
    });
  }

  _bindOverlayKeyboard = () => {
    $(document).off('keydown.galleryOverlay', this._onOverlayKeydown);
    $(document).on('keydown.galleryOverlay', this._onOverlayKeydown);
  }

  _unbindOverlayKeyboard = () => {
    $(document).off('keydown.galleryOverlay', this._onOverlayKeydown);
  }

  _onOverlayKeydown = (e) => {
    if (!this.isOverlayOpen) return;
    if ($(e.target).is('input, textarea, select, [contenteditable="true"]')) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeModal();
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.showPreviousImage();
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.showNextImage();
    }
  }

  _updateOverlayNavVisibility = () => {
    const show = galleryHasMultipleImages(this.imageItems);
    this.prevButton?.toggle(show).attr({ 'aria-hidden': show ? 'false' : 'true', tabindex: show ? '0' : '-1' });
    this.nextButton?.toggle(show).attr({ 'aria-hidden': show ? 'false' : 'true', tabindex: show ? '0' : '-1' });
  }

  _watchOverlayImageLoad = (img, src) => {
    if (!img || !src) return;
    if (img.dataset.loadWatch === src) {
      this._updateOverlayLoading();
      return;
    }
    img.dataset.loadWatch = src;
    const onDone = () => this._updateOverlayLoading();
    img.addEventListener('load', onDone, { once: true });
    img.addEventListener('error', () => {
      img.dataset.loadError = '1';
      onDone();
    }, { once: true });
    if (img.getAttribute('src') !== src) img.src = src;
    if (img.complete) onDone();
  }

  _overlayMedia = (slideEl) => slideEl?.querySelector?.('.pic__media') || (slideEl?.tagName === 'IMG' ? slideEl : null)

  _fitOverlayMedia = (wrap) => {
    const img = this._overlayMedia(wrap);
    if (!img || !wrap || wrap === img) return;
    const box = wrap.getBoundingClientRect();
    if (!img.naturalWidth || !box.width || !box.height) return;
    const rot = ((Number(wrap.dataset.rotation) || 0) % 360 + 360) % 360;
    const swapped = rot === 90 || rot === 270;
    const visW = swapped ? img.naturalHeight : img.naturalWidth;
    const visH = swapped ? img.naturalWidth : img.naturalHeight;
    const scale = Math.min(box.width / visW, box.height / visH);
    img.style.width = `${img.naturalWidth * scale}px`;
    img.style.height = `${img.naturalHeight * scale}px`;
    img.style.transform = `rotate(${rot}deg)`;
  }

  _fitVisibleOverlayMedia = () => {
    this.overlayTemplate?.[0]?.querySelectorAll('.pic').forEach((wrap) => this._fitOverlayMedia(wrap));
  }

  _onOverlayResize = () => {
    if (!this.isOverlayOpen) return;
    this._fitVisibleOverlayMedia();
  }

  _bindOverlayResize = () => {
    this._unbindOverlayResize();
    window.addEventListener('resize', this._onOverlayResize);
    window.visualViewport?.addEventListener('resize', this._onOverlayResize);
  }

  _unbindOverlayResize = () => {
    window.removeEventListener('resize', this._onOverlayResize);
    window.visualViewport?.removeEventListener('resize', this._onOverlayResize);
  }

  _updateOverlayLoading = () => {
    const $overlay = this.loadingOverlay;
    if (!$overlay?.length) return;
    const slides = this.overlayTemplate?.[0]?.querySelectorAll('.pic');
    const img = this._overlayMedia(slides?.[this.curSlide]);
    const loaded = !img || img.dataset.loadError === '1' || (img.complete && img.naturalWidth > 0);
    $overlay.prop('hidden', loaded);
    $overlay.attr('aria-busy', loaded ? 'false' : 'true');
    if (loaded && slides?.[this.curSlide]) this._fitOverlayMedia(slides[this.curSlide]);
  }

  goToSlide = (slide) => {
    this.maxSlide = this.imageItems.length;
    this.curSlide = clampGallerySlideIndex(slide, this.maxSlide);
    const root = this.overlayTemplate?.[0];
    if (!root) return;
    this.slides = root.querySelectorAll('.pic');
    const currentSlide = this.curSlide;
    this.slides.forEach((s, i) => {
      const translateXNum = 80 * (i - currentSlide);
      let translateYNum, rotateDeg, grayscaleNum, zIndexNum, opacityNum;
      if (translateXNum === 0) {
        translateYNum = 0;
        rotateDeg = 0;
        grayscaleNum = 0;
        zIndexNum = 1;
        opacityNum = 100;
      } else if (translateXNum < 0) {
        translateYNum = 5;
        rotateDeg = -5;
        grayscaleNum = 1;
        zIndexNum = 0;
        opacityNum = 0;
      } else {
        translateYNum = 5;
        rotateDeg = 5;
        grayscaleNum = 1;
        zIndexNum = 0;
        opacityNum = 0;
      }
      s.style.transform = `translate(${translateXNum}%, ${translateYNum}%) rotate(${rotateDeg}deg)`;
      s.style.filter = `grayscale(${grayscaleNum})`;
      s.style.zIndex = zIndexNum;
      s.style.opacity = `${opacityNum}%`;
      this._fitOverlayMedia(s);
    });
    this._updateOverlayLoading();
  }

  showNextImage = ({ fromAutoplay = false } = {}) => {
    if (!galleryHasMultipleImages(this.imageItems)) return;
    this.goToSlide(this.curSlide + 1);
    if (!fromAutoplay && this.isSlideshowPlaying) this.startSlideShow();
    if (this.model.iAmGuide()) {
      this.model.socket.socketTour.onTourClicked({ type: "customSlide", currSlide: this.curSlide });
    }
  }

  showPreviousImage = ({ fromAutoplay = false } = {}) => {
    if (!galleryHasMultipleImages(this.imageItems)) return;
    this.goToSlide(this.curSlide - 1);
    if (!fromAutoplay && this.isSlideshowPlaying) this.startSlideShow();
    if (tourExists(tourID, this.model.tourData) && this.model.iAmGuide()) {
      this.model.socket.socketTour.onTourClicked({ type: "customSlide", currSlide: this.curSlide });
    }
  }

  startSlideShow = () => {
    if (this.slideShowInterval) {
      clearInterval(this.slideShowInterval);
      this.slideShowInterval = null;
    }
    if (this.model.editMode) return;
    if (!galleryHasMultipleImages(this.imageItems)) return;
    this.isSlideshowPlaying = true;
    this.slideShowInterval = setInterval(() => {
      if (tourExists(tourID, this.model.tourData)) return;
      this.showNextImage({ fromAutoplay: true });
    }, getGallerySlideDurationMs(this.dbConfig));
  }

  stopSlideShow = () => {
    this.isSlideshowPlaying = false;
    if (this.slideShowInterval) {
      clearInterval(this.slideShowInterval);
      this.slideShowInterval = null;
    }
  }

  openModal = (options = {}) => {
    this._syncOverlayImages();
    this.maxSlide = this.imageItems.length;
    this.curSlide = clampGallerySlideIndex(this.curSlide, this.maxSlide);
    this._updateOverlayNavVisibility();
    this._applyOverlayControlLabels();
    this.modalWrapper.addClass("is-active");
    this.isOverlayOpen = true;
    this.goToSlide(this.curSlide);
    this._bindOverlayKeyboard();
    this._bindOverlayResize();
    requestAnimationFrame(() => this._fitVisibleOverlayMedia());
    const forcePlay = options.startSlideshow === true;
    const forcePause = options.startSlideshow === false;
    if (forcePlay || (!forcePause && shouldGalleryAutoplay(this.dbConfig, this.imageItems) && !this.model.editMode)) {
      this.startSlideShow();
    } else {
      this.stopSlideShow();
    }
  }

  closeModal = () => {
    this.modalWrapper.removeClass("is-active");
    this.isOverlayOpen = false;
    this.stopSlideShow();
    this._unbindOverlayKeyboard();
    this._unbindOverlayResize();
    if (this.model.iAmGuide()) {
      this.model.socket.socketTour.onTourClicked({ type: "closeModal" })
    }
  }

  runGalleryAction = (action, params = {}) => {
    switch (action) {
      case 'showGallery':
      case 'openModal':
        this.openModal({ startSlideshow: false });
        break;
      case 'startSlideshow':
        this.openModal({ startSlideshow: true });
        break;
      case 'nextImage':
        this.showNextImage();
        break;
      case 'previousImage':
        this.showPreviousImage();
        break;
      case 'closeGallery':
      case 'closeElement':
        this.closeModal();
        break;
      case 'showImage': {
        this.mesh.visible = true;
        this.visible = true;
        const count = this.imageItems.length;
        const imageIndex = clampGallerySlideIndex(params.imageIndex ?? 0, count);
        this.curSlide = imageIndex;
        this.maxSlide = count;
        const url = getGalleryImageUrl(this.imageItems[imageIndex]);
        if (url) this.setAvatarImage(url);
        if (this.isOverlayOpen) this.goToSlide(this.curSlide);
        break;
      }
      default:
        break;
    }
  }
  hidePerspectiveCubes = () => {
    this.children.forEach(item => {

    })
  }

  /**
   * Calculate dimensions based on aspect ratio
   * Uses 20% of screen size for the longer dimension
   * @param {number} aspectRatio - Image aspect ratio
   * @returns {{w: number, h: number}} Width and height
   */
  calculateDimensionsFromAspectRatio(aspectRatio) {
    // Get screen dimensions (20% of screen size)
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const targetSize = Math.min(screenWidth, screenHeight) * 0.2;

    let w = 1, h = 1;
    if (aspectRatio < 1) { // Portrait (height > width)
      h = targetSize;
      w = targetSize * aspectRatio;
    } else { // Landscape or square (width >= height)
      w = targetSize;
      h = targetSize / aspectRatio;
    }
    return { w, h };
  }

  /**
   * Update mesh geometry with new dimensions
   * @param {number} width - New width
   * @param {number} height - New height
   */
  updateMeshGeometry(width, height) {
    // Dispose old geometry
    if (this.mesh.geometry) {
      this.mesh.geometry.dispose();
    }
    if (this.borderMesh?.geometry) {
      this.borderMesh.geometry.dispose();
    }

    // Create new geometry for both mesh and border
    const geometry = new PlaneGeometry(width, height);
    this.mesh.geometry = geometry;
    this.mesh.geometry.computeBoundingBox();
    this.mesh.geometry.computeBoundingSphere();
    this.mesh.updateMatrixWorld(true);

    if (this.borderMesh) {
      this.borderMesh.geometry = new PlaneGeometry(width, height);
    }
  }

  /**
   * Reset perspective corner positions based on dimensions
   * @param {number} width - Mesh width
   * @param {number} height - Mesh height
   */
  resetPerspectivePositions(width, height) {
    const halfW = width / 2;
    const halfH = height / 2;
    const corners = {
      topLeft: new Vector3(-halfW, halfH, 0),
      topRight: new Vector3(halfW, halfH, 0),
      bottomRight: new Vector3(halfW, -halfH, 0),
      bottomLeft: new Vector3(-halfW, -halfH, 0)
    };

    if (!this.dbConfig.original) this.dbConfig.original = {};
    Object.entries(corners).forEach(([name, vector]) => {
      this.perspectivePosition[name] = vector.clone();
      this.dbConfig[name] = vector.clone();
      this.dbConfig.original[name] = vector.clone();
      this.originalPerspectivePosition[name] = vector.clone();
    });
  }

  resetPerspective = () => {
    this.removeDragPoints();

    if (this.useIcon) {
      this.applyIndicatorHitGeometry();
      return;
    }

    // Check if there's a valid texture with image
    const texture = this.mesh.material.map;
    const coverUrl = getGalleryCoverUrl(this.dbConfig);
    if (coverUrl) {
      if (texture && texture.image && texture.image.width && texture.image.height) {
        // Has image - reset to image's original dimensions
        let aspectRatio = texture.image.width / texture.image.height;
        if (aspectRatio === undefined || aspectRatio === 0) {
          aspectRatio = 16 / 9;
        }

        const { w, h } = this.calculateDimensionsFromAspectRatio(aspectRatio);
        this.updateMeshGeometry(w, h);
        this.resetValuesWithNewTexture(texture);
      }
    } else {
      // No image - reset to default size (64x64)
      this.updateMeshGeometry(64, 64);
      this.resetPerspectivePositions(64, 64);
    }

    this.applyPerspectiveDragAndDrop();
    if (!this.useIcon) {
      this.addDragPoints();
    }
  }

  setDbConfig = (conf) => {
    super.setDbConfig(conf);
    this.dbConfig = persistGalleryConfig(this.dbConfig);
    this.imageItems = this.dbConfig.images;
    this.images = this.imageItems.map(getGalleryImageUrl);
    const savedSize = Number(this.dbConfig?.size);
    if (Number.isFinite(savedSize) && savedSize > 0) {
      this.mesh.scale.set(savedSize, savedSize, savedSize);
    }

    Object.entries(this.perspectivePosition).forEach(([name]) => {
      if (this.perspectivePosition[name]) {
        const prop = this.dbConfig[name];
        if (prop) {
          this.perspectivePosition[name] = new Vector3(prop.x, prop.y, prop.z);
        }
      }
    });

    this.maxSlide = this.images.length;
    this.curSlide = clampGallerySlideIndex(this.curSlide, this.maxSlide);

    this.useIcon = this.dbConfig.useIcon === true;
    this.customIcon = this.useIcon === false;
    this.objConfig.isResizable = !this.useIcon;
    this.updateDragBehavior(true);

    const coverUrl = getGalleryCoverUrl(this.dbConfig);
    if (this.useIcon) {
      this.updateMeshTexture();
    } else if (this.images.length > 0 || coverUrl) {
      this._syncOverlayImages();
      if (this.isOverlayOpen) this.goToSlide(this.curSlide);
      this.updateMeshTexture();
    } else if (this.model.editMode) {
      this.updateMeshTexture();
    }

    this.stopMeshSlideShow();

    // Add drag points for existing objects loaded from JSON
    if (this.model.editMode && !this.useIcon) {
      this.addDragPoints();
    }
  }

  getDbConfig() {
    this.dbConfig = persistGalleryConfig({
      ...this.dbConfig,
      images: Array.isArray(this.imageItems) ? this.imageItems : this.dbConfig?.images,
    });
    this.imageItems = this.dbConfig.images;
    this.images = this.imageItems.map(getGalleryImageUrl);
    return this.dbConfig;
  }

  // Call this method to queue images for loading
  queueImageLoad(url) {
    this.imageLoadQueue.push(url);
    this.processImageLoadQueue(); // Start processing the queue
  }
  // Function to process the image loading queue
  processImageLoadQueue() {
    if (this.isLoadingImage || this.imageLoadQueue.length === 0) {
      // If an image is currently loading or there are no images to load, do nothing
      return;
    }
    // Get the next image URL from the queue and load it
    const nextImageUrl = this.imageLoadQueue.shift();
    this.isLoadingImage = true; // Set the flag to indicate that an image is loading

    // Load the image and pass the callback function
    this.setAvatarImage(nextImageUrl, () => {
      this.isLoadingImage = false; // Reset the flag after the image has loaded
      this.processImageLoadQueue(); // Recursively process the next image in the queue
    });
  }

  setAvatarImage(url, callback, newImage) {
    if (this.useIcon) {
      this.material.map = emptyTexture;
      this.material.needsUpdate = true;
      callback && callback();
      return;
    }
    url = getGalleryImageUrl(url);
    if (!url) {
      callback && callback();
      return;
    }
    if (meshTextures[url]) {
      const texture = meshTextures[url];
      this.material.map = texture;

      // Keep geometry in sync with texture dimensions even when texture is cached.
      if (texture?.image?.width && texture?.image?.height) {
        let aspectRatio = texture.image.width / texture.image.height;
        if (aspectRatio === undefined || aspectRatio === 0) {
          aspectRatio = 16 / 9;
        }
        const { w, h } = this.calculateDimensionsFromAspectRatio(aspectRatio);
        this.updateMeshGeometry(w, h);
      }

      // Reset corners only when caller explicitly loads a new base image.
      if (newImage) {
        this.resetValuesWithNewTexture(texture);
      }

      this.applyPerspectiveDragAndDrop();
      callback && callback();
      return;
    }
    !this.isLoading && this.loader.load(url, (texture) => {
      this.isLoading = false;
      this.imageJustLoaded = true;
      if (this.useIcon) {
        this.material.map = emptyTexture;
        this.material.needsUpdate = true;
        callback && callback();
        return;
      }

      let aspectRatio = texture.image.width / texture.image.height;
      if (aspectRatio === undefined || aspectRatio === 0) {
        aspectRatio = 16 / 9; // Default to a common aspect ratio if undefined or zero
      }
      // Calculate dimensions and update geometry
      const { w, h } = this.calculateDimensionsFromAspectRatio(aspectRatio);
      meshTextures[url] = texture;
      this.material.map = texture;
      this.updateMeshGeometry(w, h);

      // Reset corners only when caller explicitly loads a new base image.
      if (newImage) {
        this.resetValuesWithNewTexture(texture);
      }

      if (callback) { callback(); }

      this.removeDragPoints();
      this.applyPerspectiveDragAndDrop();
      this.model.editMode && this.addDragPoints();
      $('.slider').trigger('change');
    });
  }

  resetValuesWithNewTexture(texture) {
    let aspectRatio = texture.image.width / texture.image.height;
    if (aspectRatio === undefined || aspectRatio === 0) {
      aspectRatio = 16 / 9;
    }

    const { w, h } = this.calculateDimensionsFromAspectRatio(aspectRatio);
    this.resetPerspectivePositions(w, h);
  }

  applyPerspectiveDragAndDrop() {
    const points = this.perspectivePosition;
    const positionAttribute = this.mesh.geometry.attributes.position;
    const uvAttribute = this.mesh.geometry.attributes.uv;

    // Mapping the corners to the indices use based on their order in the mesh's vertices
    const cornerMappings = {
      topLeft: 0,
      topRight: 1,
      bottomRight: 3,
      bottomLeft: 2
    };

    // Loop through each corner and update its position
    Object.entries(cornerMappings).forEach(([corner, index]) => {
      if (points[corner]) {
        positionAttribute.setXY(index, points[corner].x, points[corner].y);

        if (this.dragCubeCorners && this.dragCubeCorners[corner]) {
          const handle = cornerHandleLocalFromVertex(points[corner], this.objConfig, corner);
          this.dragCubeCorners[corner].position.set(handle.x, handle.y, handle.z);
        }
      }
    });

    // Update UVs to standard mapping
    uvAttribute.setXY(cornerMappings['topLeft'], 0, 1); // UV for topLeft
    uvAttribute.setXY(cornerMappings['topRight'], 1, 1); // UV for topRight
    uvAttribute.setXY(cornerMappings['bottomRight'], 1, 0); // UV for bottomRight
    uvAttribute.setXY(cornerMappings['bottomLeft'], 0, 0); // UV for bottomLeft

    positionAttribute.needsUpdate = true;
    uvAttribute.needsUpdate = true; // Notify Three.js that the UVs have changed

    // Update bounding box after deforming geometry
    this.mesh.geometry.computeBoundingBox();
    this.mesh.geometry.computeBoundingSphere();

    this.applyPerspectiveDragAndDropBorder()
  }
  applyPerspectiveDragAndDropBorder() {
    const points = this.perspectivePosition;
    const positionAttribute = this.borderMesh.geometry.attributes.position;
    const uvAttribute = this.borderMesh.geometry.attributes.uv;

    // Mapping the corners to the indices based on their order in the mesh's vertices
    const cornerMappings = {
      topLeft: 0,
      topRight: 1,
      bottomRight: 3,
      bottomLeft: 2
    };

    // Loop through each corner and update its position
    Object.entries(cornerMappings).forEach(([corner, index]) => {
      if (points[corner]) {
        positionAttribute.setXY(index, points[corner].x, points[corner].y);
      }
    });

    // Update UVs to standard mapping
    uvAttribute.setXY(cornerMappings['topLeft'], 0, 1); // UV for topLeft
    uvAttribute.setXY(cornerMappings['topRight'], 1, 1); // UV for topRight
    uvAttribute.setXY(cornerMappings['bottomRight'], 1, 0); // UV for bottomRight
    uvAttribute.setXY(cornerMappings['bottomLeft'], 0, 0); // UV for bottomLeft

    positionAttribute.needsUpdate = true;
    uvAttribute.needsUpdate = true; // Notify Three.js that the UVs have changed

  }


  updateLivePosition(name, position) {
    this.dbConfig[name] = this.perspectivePosition[name].clone();
    this.perspectivePosition[name].copy(position); // Assume position is already in local space
    this.applyPerspectiveDragAndDrop(); // Apply changes to the mesh
  }

  updateMeshCorner(cornerName, newPosition) {
    const positionAttribute = this.mesh.geometry.attributes.position;
    const cornerMappings = {
      topLeft: 0,
      topRight: 1,
      bottomRight: 3,
      bottomLeft: 2
    };

    const vertex = vertexLocalFromCornerHandle(newPosition, this.objConfig, cornerName);
    const vertexPosition = new Vector3(vertex.x, vertex.y, vertex.z);

    this.perspectivePosition[cornerName] = vertexPosition;
    this.dbConfig[cornerName] = vertexPosition.clone();

    const vertexIndex = cornerMappings[cornerName];
    if (vertexIndex !== undefined) {
      positionAttribute.setXYZ(
        vertexIndex,
        vertexPosition.x,
        vertexPosition.y,
        vertexPosition.z
      );
      positionAttribute.needsUpdate = true;
      this.mesh.geometry.computeBoundingBox();
      this.mesh.geometry.computeBoundingSphere();
    }
  }
  onSelect() {
    if (this.useIcon) {
      super.onSelect();
      return;
    }
    // Show drag handles when object is selected
    this.showDragHandles();
    // Hide border in edit mode
    if (this.model.editMode && this.borderMesh) {
      this.borderMesh.visible = false;
    }
    super.onSelect();
  }

  onDeselect() {
    // Hide drag handles when object is deselected
    this.hideDragHandles();
    super.onDeselect();
  }

  showDragHandles() {
    if (this.useIcon) return;
    Object.values(this.dragSprites).forEach(sprite => {
      if (sprite) sprite.visible = true;
    });
    Object.values(this.dragCubeCorners).forEach(cube => {
      if (cube) cube.visible = true;
    });
    if (this.centerHandle) this.centerHandle.visible = true;
    if (this.centerHandleSprite) this.centerHandleSprite.visible = true;
  }

  hideDragHandles() {
    Object.values(this.dragSprites).forEach(sprite => {
      if (sprite) sprite.visible = false;
    });
    Object.values(this.dragCubeCorners).forEach(cube => {
      if (cube) cube.visible = false;
    });
    if (this.centerHandle) this.centerHandle.visible = false;
    if (this.centerHandleSprite) this.centerHandleSprite.visible = false;
  }

  addDragPoints() {
    if (this.useIcon) return;
    if (this.dragCubeCorners["topLeft"]) {
      return;
    }
    const sphereRadius = this.objConfig.cubeSize / 2;
    const cubeGeometry = new SphereGeometry(sphereRadius, 8, 8);
    const cubeMaterial = new MeshBasicMaterial({
      color: 0x6B9D11,
      opacity: 0,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      renderOrder: 999,
    });

    // Check if the mesh's geometry's positions are accessible
    if (!this.mesh.geometry.attributes.position) {
      console.error("Mesh geometry does not have position attributes.");
      return;
    }

    const positionAttribute = this.mesh.geometry.attributes.position;
    if (positionAttribute.count < 4) {
      console.error("Mesh does not have enough vertices to define four corners.");
      return;
    }

    // Define corner positions by the first four vertices in the geometry
    // This assumes the vertices are laid out as:
    // 0 - topLeft, 1 - topRight, 3 - bottomRight, 2 - bottomLeft (depending on the draw order)
    const cornerNames = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
    const cornerIndices = [0, 1, 3, 2];  // Adjust these indices according to your specific geometry

    this.dragCubeCorners = {};  // Store references to the corner cubes

    cornerIndices.forEach((index, i) => {
      const offset = this.objConfig.offset[cornerNames[i]]

      // Access vertex positions
      const x = positionAttribute.getX(index) + (offset?.x || 0);
      const y = positionAttribute.getY(index) + (offset?.y || 0);
      const z = positionAttribute.getZ(index);

      // Create cube and position it at the vertex
      const cube = new Mesh(cubeGeometry, cubeMaterial);

      cube.name = cornerNames[i];  // Name the cube based on its corner
      cube.position.set(x, y + this.objConfig.cubeSize / 2, z);

      // Create spherical background sprite for better raycasting from all angles
      const spriteRadius = this.objConfig.cubeSize / 2 * 1.1;
      const spriteGeometry = new SphereGeometry(spriteRadius, 16, 16);
      const spriteMaterial = new MeshBasicMaterial({
        color: 0x6B9D11, // Green color
        transparent: true,
        opacity: 0.5,
        depthTest: false
      });

      const sprite = new Mesh(spriteGeometry, spriteMaterial);
      sprite.name = `sprite_${cornerNames[i]}`;
      sprite.position.set(0, -this.objConfig.cubeSize / 2, 0);
      sprite.raycast = () => { };

      // Create icon sprite on top of circle
      const spriteIconMaterial = new SpriteMaterial({
        map: myLocationTexture,
        depthTest: false,
        transparent: true
      });
      const spriteIcon = new Sprite(spriteIconMaterial);
      spriteIcon.name = `spriteIcon_${cornerNames[i]}`;
      spriteIcon.position.set(0, -this.objConfig.cubeSize / 2, 0.1);
      spriteIcon.scale.set(this.objConfig.cubeSize / 2, this.objConfig.cubeSize / 2, this.objConfig.cubeSize / 2);
      spriteIcon.raycast = () => { };

      cube.add(sprite);
      cube.add(spriteIcon);

      // Store the sprite reference for future access
      this.dragSprites[sprite.name] = sprite;

      // Add the cube to the mesh so it inherits transformations
      this.mesh.add(cube);

      // Store the cube reference for future access
      this.dragCubeCorners[cube.name] = cube;
    });

    // Create center handle (sphere)
    const sphereGeometry = new SphereGeometry(sphereRadius, 8, 8);
    const sphereMaterial = new MeshBasicMaterial({
      color: 0x6B9D11, // green color
      opacity: 0,
      transparent: true
    });

    this.centerHandle = new Mesh(sphereGeometry, sphereMaterial);
    this.centerHandle.name = 'centerHandle';
    this.centerHandle.position.set(0, 0, 0); // Center of the mesh
    this.centerHandle.raycast = () => { };

    // Create spherical background for center handle for better raycasting
    const centerSpriteRadius = sphereRadius * 1.1;
    const centerSpriteGeometry = new SphereGeometry(centerSpriteRadius, 16, 16);
    const centerSpriteMaterial = new MeshBasicMaterial({
      color: 0x6B9D11, // green color
      transparent: true,
      opacity: 0.5,
      depthTest: false
    });
    this.centerHandleSprite = new Mesh(centerSpriteGeometry, centerSpriteMaterial);
    this.centerHandleSprite.name = 'sprite_centerHandle';
    this.centerHandleSprite.position.set(0, 0, 0);
    this.centerHandleSprite.raycast = () => { };

    // Create icon sprite on top of circle
    const centerSpriteIconMaterial = new SpriteMaterial({
      map: dragTexture,
      depthTest: false,
      transparent: true
    });
    this.centerHandleSpriteIcon = new Sprite(centerSpriteIconMaterial);
    this.centerHandleSpriteIcon.name = 'spriteIcon_centerHandle';
    this.centerHandleSpriteIcon.position.set(0, 0, 0.1); // Slightly in front
    this.centerHandleSpriteIcon.scale.set(sphereRadius, sphereRadius, sphereRadius);
    this.centerHandleSpriteIcon.raycast = () => { };

    this.centerHandle.add(this.centerHandleSprite);
    this.centerHandle.add(this.centerHandleSpriteIcon);
    this.mesh.add(this.centerHandle);

    // Initially hide all handles
    this.hideDragHandles();
  }
  animateBorder() {
    this.borderMesh.material.opacity = .25;
  }
  removeBorder() {
    this.borderMesh.material.opacity = 0;
  }
  removeDragPoints() {
    Object.entries(this.dragSprites).forEach(([name, sprite]) => {
      if (this.dragSprites[name]) {
        this.dragSprites[name].parent?.remove(this.dragSprites[name]);
        this.dragSprites[name] = null;
      }
    });
    Object.entries(this.dragCubeCorners).forEach(([name, corner]) => {
      this.mesh.remove(this.dragCubeCorners[name]);
      this.dragCubeCorners[name] = null;
    });

    // Remove center handle
    if (this.centerHandle) {
      this.mesh.remove(this.centerHandle);
      this.centerHandle = null;
    }
    if (this.centerHandleSprite) {
      this.centerHandleSprite = null;
    }
    if (this.centerHandleSpriteIcon) {
      this.centerHandleSpriteIcon = null;
    }
  }

}

export { VRImageGallery };
